import { Box, Flex } from "@chakra-ui/react"
import { IconName } from "@pathwright/pathicons"
import { LightMode } from "../chakra/ui/color-mode"
import Pathicon from "../pathicon/Pathicon"
import {
  ClearIndicatorProps,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  MenuProps,
  MultiValueRemoveProps,
  chakraComponents
} from "chakra-react-select"
import { ReactNode } from "react"

export type CustomSelectProps = {
  icon?: IconName
  renderControlLeftElement?: () => ReactNode
  /** Renders at the control's trailing edge, after the indicators. */
  renderControlRightElement?: () => ReactNode
}

type WithCustomSelectProps<
  T extends any,
  TCustomSelectProps extends CustomSelectProps
> = T & {
  selectProps: TCustomSelectProps
}

export function getSelectCustomComponents<
  Option,
  IsMulti extends boolean,
  TCustomSelectProps extends CustomSelectProps = CustomSelectProps
>() {
  type Group = GroupBase<Option>

  const ClearIndicator = (
    props: WithCustomSelectProps<
      ClearIndicatorProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => (
    <chakraComponents.ClearIndicator {...props}>
      <Pathicon icon="x" size="1.4em" />
    </chakraComponents.ClearIndicator>
  )

  const DropdownIndicator = (
    props: WithCustomSelectProps<
      DropdownIndicatorProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => {
    if (
      props.selectProps.options?.length === 0 ||
      (props.selectProps.options?.length === 1 &&
        (props.selectProps.options[0] as { id: number }).id === -1)
    ) {
      return null
    }

    return (
      <chakraComponents.DropdownIndicator {...props}>
        <Pathicon icon="chevron-down" size="1em" />
      </chakraComponents.DropdownIndicator>
    )
  }

  const MultiValueRemove = (
    props: WithCustomSelectProps<
      MultiValueRemoveProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => (
    <chakraComponents.MultiValueRemove {...props}>
      <Flex cursor="pointer">
        <Pathicon icon="x" size=".85em" />
      </Flex>
    </chakraComponents.MultiValueRemove>
  )

  const Menu = (
    props: WithCustomSelectProps<
      MenuProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => {
    if (props.selectProps.options?.length === 0) {
      return null
    }

    return (
      <LightMode>
        <chakraComponents.Menu {...props} />
      </LightMode>
    )
  }

  const Control = (
    props: WithCustomSelectProps<
      ControlProps<Option, IsMulti, Group>,
      TCustomSelectProps
    >
  ) => {
    const iconElement = !!props.selectProps.icon && (
      <Pathicon icon={props.selectProps.icon} />
    )
    // `undefined` falls back to the icon; `null` opts out entirely, which is how
    // callers suppress the icon when the selected value renders its own visual.
    const renderedLeftElement = props.selectProps.renderControlLeftElement?.()
    const leftElement =
      renderedLeftElement === undefined ? iconElement : renderedLeftElement
    const rightElement = props.selectProps.renderControlRightElement?.()

    return (
      <chakraComponents.Control {...props}>
        {!!leftElement && (
          <Box
            width="var(--input-height)"
            height="var(--input-height)"
            display="flex"
            alignItems="center"
            justifyContent="center"
          >
            {leftElement}
          </Box>
        )}
        {props.children}
        {!!rightElement && (
          <Box display="flex" alignItems="center" flexShrink={0}>
            {rightElement}
          </Box>
        )}
      </chakraComponents.Control>
    )
  }

  return {
    Menu,
    ClearIndicator,
    DropdownIndicator,
    MultiValueRemove,
    Control
  }
}
